import { ShieldCheck, FileCheck2, BadgeCheck, Eye, Award, Scale } from "lucide-react";
import { ALLIES, IMAGES } from "../data/site";
import { SectionEyebrow } from "../components/SectionEyebrow";

export default function Transparencia() {
    return (
        <>
            <section className="topo-bg py-20 lg:py-28" data-testid="transparency-hero">
                <div className="container-fe grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <SectionEyebrow>Transparencia</SectionEyebrow>
                        <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-[1.05]">
                            La confianza se <span className="text-brand-green">demuestra</span>.
                        </h1>
                        <p className="mt-7 text-lg text-muted-foreground leading-relaxed">
                            La transparencia no es un eslogan: es una práctica diaria que sostiene cada
                            decisión que tomamos. Aquí te mostramos cómo trabajamos, cómo invertimos y por
                            qué puedes confiar en nosotros.
                        </p>
                    </div>
                    <div className="relative">
                        <div className="rounded-3xl bg-white border border-black/5 p-8 shadow-2xl">
                            <div className="flex items-start gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-brand-green text-white flex items-center justify-center shrink-0">
                                    <BadgeCheck size={28} />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider font-bold text-brand-green">Reconocimiento</div>
                                    <h3 className="mt-2 font-display font-bold text-2xl text-brand-navy">Programa Aflora</h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                        Otorgado por la Fundación Bolívar Davivienda a organizaciones que
                                        cumplen altos estándares de gobernanza, ética y rendición de cuentas.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 pt-6 border-t border-black/5 grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="font-display text-3xl font-bold text-brand-green">87%</div>
                                    <div className="text-xs text-muted-foreground mt-1">Inversión en programas</div>
                                </div>
                                <div>
                                    <div className="font-display text-3xl font-bold text-brand-green">100%</div>
                                    <div className="text-xs text-muted-foreground mt-1">Auditoría externa</div>
                                </div>
                                <div>
                                    <div className="font-display text-3xl font-bold text-brand-green">15</div>
                                    <div className="text-xs text-muted-foreground mt-1">Años sin sanciones</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pillars */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe">
                    <div className="max-w-2xl">
                        <SectionEyebrow>Nuestras buenas prácticas</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Seis compromisos que nos hacen confiables.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: <ShieldCheck size={24} />, t: "Gobernanza ética", d: "Junta directiva activa, código de ética público y procedimientos claros para conflictos de interés." },
                            { icon: <FileCheck2 size={24} />, t: "Rendición de cuentas", d: "Informes anuales, estados financieros auditados y reportes de impacto disponibles bajo solicitud." },
                            { icon: <Eye size={24} />, t: "Trazabilidad de recursos", d: "Cada donación está vinculada a un proyecto específico. El donante puede solicitar el destino exacto." },
                            { icon: <Scale size={24} />, t: "Cumplimiento legal", d: "Registros vigentes en Cámara de Comercio, DIAN y entidades de control. Cero sanciones en 15 años." },
                            { icon: <BadgeCheck size={24} />, t: "Aflora — Bolívar Davivienda", d: "Reconocimiento institucional que valida nuestra gestión integral, ética y rigurosa." },
                            { icon: <Award size={24} />, t: "Alianzas validadas", d: "Trabajamos con universidades, gobierno local, empresas y voluntariado internacional bajo convenios formales." },
                        ].map((it) => (
                            <div key={it.t} className="card-flat" data-testid={`transparency-pillar-${it.t}`}>
                                <div className="h-12 w-12 rounded-xl bg-brand-green-light text-brand-green flex items-center justify-center">
                                    {it.icon}
                                </div>
                                <h3 className="mt-5 font-display text-xl font-semibold text-brand-navy">{it.t}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Allies */}
            <section className="py-20 lg:py-28 bg-brand-cream" data-testid="allies-section">
                <div className="container-fe">
                    <div className="max-w-2xl">
                        <SectionEyebrow>Aliados estratégicos</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Quienes caminan con nosotros.
                        </h2>
                        <p className="mt-5 text-muted-foreground leading-relaxed text-lg">
                            Las alianzas son el motor de nuestro trabajo. Cada organización que se suma
                            multiplica el impacto en el territorio.
                        </p>
                    </div>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {ALLIES.map((a) => (
                            <div key={a.name} className="rounded-2xl bg-white border border-black/5 p-7 hover:border-brand-green/30 hover:-translate-y-1 transition-all">
                                <div className="h-14 w-14 rounded-xl bg-brand-navy text-white flex items-center justify-center font-display font-bold text-xl">
                                    {a.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                                </div>
                                <h3 className="mt-4 font-display font-semibold text-brand-navy">{a.name}</h3>
                                <p className="text-sm text-muted-foreground mt-1">{a.program}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Documents CTA */}
            <section className="py-20 lg:py-28 bg-brand-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 grain-bg opacity-30" />
                <div className="container-fe relative grid lg:grid-cols-2 gap-12 items-center">
                    <div className="aspect-[4/5] rounded-3xl overflow-hidden ring-4 ring-white/10">
                        <img src={IMAGES.elderly_care} alt="Confianza institucional" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <SectionEyebrow className="[&>span:last-child]:text-brand-green">Documentación pública</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl leading-tight">
                            Pídenos los informes. Estamos listos.
                        </h2>
                        <p className="mt-6 text-white/80 leading-relaxed text-lg">
                            Los siguientes documentos están disponibles para cualquier persona que quiera revisarlos:
                        </p>
                        <ul className="mt-6 space-y-3">
                            {["Estados financieros auditados (anuales)", "Informe de gestión y de impacto", "Acta de constitución y estatutos", "Certificado de existencia vigente", "Política de protección de datos personales"].map((t) => (
                                <li key={t} className="flex items-start gap-3 text-white/85">
                                    <FileCheck2 size={18} className="text-brand-green mt-0.5 shrink-0" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                        <a
                            href="mailto:direccion_general@formarestructura.org"
                            className="btn-primary-pill mt-8 bg-white !text-brand-navy hover:bg-brand-cream"
                            data-testid="transparency-request-docs"
                        >
                            Solicitar documentos
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
}
